// Аналитика Mini App — Google Analytics 4 (веб-поток футбольного проекта Firebase). Единственное место, что трогает gtag
// и dataLayer. Включается только идентификатором потока из окружения (VITE_GA_MEASUREMENT_ID); без него, в dev и в
// mock-режиме — тихо ничего не делает. Режим проверки (npm run dev:check) — dev-сервер с моком, аналитика включена,
// каждое событие с debug_mode (видно в DebugView); в боевую сборку флаг не попадает (vite.config).
// Номер Telegram и имя человека в аналитику НЕ уходят (Р-9): клиент — наш идентификатор установки (тот же X-Device-Id),
// человек — user_id = analytics_id из ответа входа; куки не используются (в окне Telegram Web они сторонние).
// События словаря v1 (MLS-Backend docs/analytics/events.yaml): tg_launch, screen_view, content_open (useContentOpen),
// tab_select, match_detail_tab_select, match_center_filter, действия a_* (по адресу пишущего запроса).
import { DEVICE_ID, setActionListener } from '../api/client';
import { lang } from '../i18n/i18n';
import { splitStartParam } from '../deeplink';
import { isMock, isTelegram, startParam, telegramPlatform } from '../telegram/webapp';
import { actionName, actionResult, campaignOf, campaignQuery, launchSource, screenContentId, screenName } from './events';
import { Profile } from './profile';
import { savedAnalyticsUser, setSink, setUserSink, track, type Params } from './track';

declare global { interface Window { dataLayer?: unknown[] } }

/** Режим проверки: true только на dev-сервере в режиме check (define в vite.config); в сборке — литерал false. */
declare const __ANALYTICS_DEBUG__: boolean;

const ID: string = import.meta.env.VITE_GA_MEASUREMENT_ID ?? '';
const enabled = Boolean(ID) && (__ANALYTICS_DEBUG__ || (!import.meta.env.DEV && !isMock));
/** Номер релиза сборки (scripts/deploy.sh: VITE_RELEASE); вне выката — dev. */
export const APP_VERSION: string = import.meta.env.VITE_RELEASE || 'dev';
/** Сколько события ждут ответа входа (user_id = analytics_id на первом же событии); дольше — уходят без него. */
const USER_WAIT_MS = 4000;

function gtag(..._args: unknown[]): void {
  // gtag.js читает именно объект arguments, не массив
  window.dataLayer!.push(arguments);
}

let profile: Profile | null = null;
let utm = '';
let lastScreen = '';

/** Контекст каждого события: где человек и насколько он «старый»; спорт и релиз — обязательные поля веба (словарь §2). */
const context = (): Params => ({
  screen: lastScreen || undefined, age_d: profile?.ageDays(Date.now()), sess_n: profile?.sessions,
  sport: 'football', app_version: APP_VERSION,
  ...(__ANALYTICS_DEBUG__ ? { debug_mode: true } : {}),
});

/** Откуда пришли на запись истории (ключ location маршрутизатора): экран, на котором нажали, — один раз, при первом
 *  появлении записи. Возврат «назад» к той же записи сохраняет её источник. */
const sources = new Map<string, string>();
let lastContent: string | undefined;
export interface ScreenLocation { pathname: string; search: string; key: string }

/** Экран показан: screen_view (один раз на показ — смена имени или номера сущности) и источник записи истории.
 *  from — источник новой записи явно (холодный старт по ссылке — link), иначе — экран, на котором был человек. */
export function trackScreen(loc: ScreenLocation, from?: string): void {
  if (!sources.has(loc.key)) sources.set(loc.key, from ?? (lastScreen || 'unattributed'));
  const name = screenName(loc.pathname);
  // номер матча / игрока / турнира — параметром, не в имени экрана
  const id = screenContentId(loc.pathname);
  if (name === lastScreen && id === lastContent) return;
  const prev = lastScreen;
  lastScreen = name;
  lastContent = id;
  track('screen_view', { screen_name: name, prev_screen: prev || undefined, content_id: id });
}

/** source для content_open: экран, на котором нажали (словарь v1, §4.2); неизвестно — unattributed. */
export const sourceOf = (key: string): string => sources.get(key) ?? 'unattributed';

/** Обезличенный номер пользователя (analytics_id сервера, НЕ номер Telegram) — склейка с данными сервера. */
export function setAnalyticsUser(id: string | number | null | undefined): void {
  if (enabled && id != null && id !== '') gtag('config', ID, { user_id: String(id), send_page_view: false });
}

/** Параметры tg_launch: метка из start_param (Р-7: «__bot-<тип>» → bot / notification / <тип>) и вид назначения. */
export function launchParams(param: string | undefined, isNew: boolean): Params {
  const { dest, label } = splitStartParam(param);
  const c = campaignOf(label, dest);
  return { source: c.source, medium: c.medium, campaign: c.name, content: c.content || undefined,
    dest: dest ? launchSource(dest) : 'home', tg_platform: telegramPlatform(), is_new: isNew ? 1 : 0 };
}

/** userReady — вход завершился (успехом или гостем): до него (не дольше USER_WAIT_MS) события копятся в очереди, чтобы
 *  первое же уходило с user_id. */
export function initAnalytics(userReady: Promise<unknown> = Promise.resolve()): void {
  if (!enabled) { setSink(null); return; }
  let storage: Storage | null = null;
  try { storage = window.localStorage; } catch { /* приватный режим */ }
  const now = Date.now();
  profile = new Profile(storage, now);
  const param = startParam();
  const { dest, label } = splitStartParam(param);
  const session = campaignOf(label, dest);
  const first = profile.touch(session, now);
  utm = campaignQuery(session);
  const launch = launchParams(param, profile.isNew);

  window.dataLayer = window.dataLayer ?? [];
  gtag('js', new Date());
  gtag('config', ID, {
    send_page_view: false, client_id: DEVICE_ID, client_storage: 'none', allow_google_signals: false, allow_ad_personalization_signals: false,
    // кампания сессии: Google читает utm из адреса «страницы»; явные поля — на случай, если адрес не разберёт.
    // Адрес — без hash: в нём данные запуска Telegram (tgWebAppData), их слать нельзя.
    page_location: `${location.origin}/?${utm}`,
    campaign_source: session.source, campaign_medium: session.medium, campaign_name: session.name, campaign_content: session.content || undefined,
    user_properties: {
      client: 'telegram', tg_platform: telegramPlatform(), app_language: lang, in_telegram: isTelegram,
      // первое касание — навсегда: по нему считается окупаемость рекламы
      first_source: first.source, first_medium: first.medium, first_campaign: first.name, first_content: first.content || undefined,
    },
    ...(__ANALYTICS_DEBUG__ ? { debug_mode: true } : {}),
  });
  setUserSink(setAnalyticsUser);
  setAnalyticsUser(savedAnalyticsUser());
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ID)}`;
  document.head.appendChild(s);
  const open = () => setSink((name, params) => {
    // экран — «страницей» с метками кампании: Google раскладывает просмотры по кампаниям сессии
    const page: Params = name === 'screen_view' && typeof params.screen_name === 'string'
      ? { page_title: params.screen_name, page_location: `${location.origin}/${params.screen_name}?${utm}` } : {};
    gtag('event', name, { ...context(), ...page, ...params });
  }, [['tg_launch', launch]]); // tg_launch — первым событием сессии, затем накопленное до запуска
  let opened = false;
  const once = () => { if (!opened) { opened = true; open(); } };
  void userReady.then(once, once);
  setTimeout(once, USER_WAIT_MS);
  setActionListener((a) => {
    const name = actionName(a.method, a.path);
    if (name) track(name, { result: actionResult(a.status, a.code, a.reason) });
  });
}
